import { Link, Navigate } from "@tanstack/react-router";
import type {
  BorrowerCaseView,
  CaseStage,
  CaseView,
  DealerCaseView,
  MeResponse,
  TexCaseView,
} from "@tex/finance-shared";
import { type FormEvent, useMemo, useState } from "react";
import {
  BuyerFields,
  DeliveryFields,
  EquipmentEditor,
  PricingEditor,
  PricingSummary,
} from "../components/case-form";
import { TimelineCard } from "../components/case-activity";
import { ClosingCard } from "../components/closing-card";
import { DocumentsCard } from "../components/documents-card";
import { EligibilityCard } from "../components/eligibility-card";
import { AddresseeInformationRequestsCard } from "../components/information-requests";
import { OffersCard } from "../components/offers-card";
import { ParticipantsCard } from "../components/participants-card";
import {
  Alert,
  Button,
  Card,
  Field,
  Mono,
  Row,
  StageBadge,
  Textarea,
} from "../components/ui";
import { ApiError, errorMessage } from "../lib/api";
import {
  type BuyerDraft,
  buyerDraftFrom,
  type DeliveryDraft,
  deliveryDraftFrom,
  ENTITY_TYPE_LABELS,
  type EquipmentDraft,
  equipmentDraftFrom,
  FormError,
  type PricingDraft,
  pricingDraftFrom,
  pricingReasonMessage,
  previewPricing,
  SOFT_COST_LABELS,
  toBuyerInput,
  toDeliveryInput,
  toEquipmentInput,
  toPricingInput,
} from "../lib/case-form";
import { useApplication } from "../lib/application";
import { STEP_LABELS } from "../lib/application-form";
import {
  useCase,
  useReplaceEquipment,
  useUpdateLead,
  useUpdatePricing,
  useWithdrawCase,
} from "../lib/cases";
import { formatAmount, formatDate, formatDay, humanize } from "../lib/format";
import { CLOSING_STAGES } from "../lib/closing";
import { OFFER_STAGES } from "../lib/offers";
import { useMe } from "../lib/session";
import { DiscrepancyList } from "./application";

/** Mirrors DEALER_EDITABLE_STAGES / WITHDRAWABLE_STAGES in the API; the server still decides. */
const EDITABLE_STAGES: readonly CaseStage[] = ["lead", "application"];
const WITHDRAWABLE_STAGES: readonly CaseStage[] = [
  "lead",
  "application",
  "review",
  "lender_review",
  "offer_review",
];

type StaffOrDealerView = DealerCaseView | TexCaseView;

function permissionsFor(me: MeResponse, view: StaffOrDealerView) {
  const stageEditable = EDITABLE_STAGES.includes(view.stage);
  const stageWithdrawable = WITHDRAWABLE_STAGES.includes(view.stage);
  if (view.audience === "tex")
    return {
      canEdit: false,
      canWithdraw: stageWithdrawable,
      canInvite: false,
      canRevoke: false,
      canUploadDocuments: true,
      readOnlyReason: null,
    };
  const membership = me.dealers.find((d) => d.dealer_id === view.dealer_id);
  if (membership?.status !== "active") {
    return {
      canEdit: false,
      canWithdraw: false,
      canInvite: false,
      canRevoke: false,
      canUploadDocuments: false,
      readOnlyReason:
        "Your dealership is not active, so this request is read-only.",
    };
  }
  return {
    canEdit: stageEditable,
    canWithdraw: stageWithdrawable,
    canInvite: stageEditable,
    canRevoke: true,
    canUploadDocuments: true,
    readOnlyReason: stageEditable
      ? null
      : view.stage === "closed"
        ? "This request is closed and can no longer be edited."
        : "This request has moved past the application stage; Tex owns changes from here.",
  };
}

function writeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "version_conflict") {
      return "Someone else changed this request first. Your edits are kept; save again to apply them over the latest version, or cancel to see what changed.";
    }
    if (err.code === "pricing_invalid")
      return pricingReasonMessage(err.reason) ?? errorMessage(err);
  }
  return errorMessage(err);
}

export function CaseDetailPage({ caseId }: { caseId: string }) {
  const me = useMe();
  const kase = useCase(caseId);

  if (me.data === null) return <Navigate to="/sign-in" />;
  if (kase.isPending || !me.data)
    return <p className="text-sm text-neutral-500">Loading…</p>;
  if (kase.isError) {
    return (
      <div className="space-y-3">
        <Alert tone="error">{errorMessage(kase.error)}</Alert>
        <Link
          to="/cases"
          search={{}}
          className="text-sm text-neutral-500 hover:underline"
        >
          ← Back to requests
        </Link>
      </div>
    );
  }
  const view: CaseView = kase.data;
  if (view.audience === "borrower") return <BorrowerCaseDetail view={view} />;
  return <CaseDetail me={me.data} view={view} />;
}

/** What the buyer sees: their dealer's branding, the equipment and the amount requested — no dealer-side notes. */
function BorrowerCaseDetail({ view }: { view: BorrowerCaseView }) {
  return (
    <section className="space-y-6">
      <div>
        <Link
          to="/my-cases"
          className="text-sm text-neutral-500 hover:text-neutral-800"
        >
          ← Your financing requests
        </Link>
        <h1 className="mt-1 flex flex-wrap items-center gap-3 font-display text-3xl font-bold text-neutral-900">
          <span
            style={
              view.dealer.accent_color
                ? { color: view.dealer.accent_color }
                : undefined
            }
          >
            {view.dealer.display_name}
          </span>
          <StageBadge stage={view.stage} />
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Financing request <Mono>{view.reference}</Mono> for{" "}
          {view.buyer_business_name} · opened {formatDate(view.created_at)}
        </p>
      </div>

      <BorrowerApplicationCard view={view} />
      {OFFER_STAGES.includes(view.stage) && (
        <OffersCard caseId={view.id} audience="borrower" stage={view.stage} />
      )}
      {CLOSING_STAGES.includes(view.stage) && (
        <ClosingCard caseId={view.id} audience="borrower" />
      )}

      <Card title="What is being financed">
        <ul className="space-y-2 text-sm">
          {view.equipment.map((e) => (
            <li
              key={e.id}
              className="flex flex-wrap items-baseline justify-between gap-2 border-b border-neutral-100 pb-2 last:border-0"
            >
              <span>
                <span className="font-medium">
                  {[e.model_year, e.make, e.model].filter(Boolean).join(" ") ||
                    e.category}
                </span>
                <span className="text-neutral-500">
                  {" "}
                  · {humanize(e.condition)} · qty {e.quantity}
                </span>
              </span>
              <Mono>{formatAmount(e.line_total)}</Mono>
            </li>
          ))}
        </ul>
        <dl className="mt-4">
          <Row
            label="Invoice total"
            value={<Mono>{formatAmount(view.pricing.invoice_total)}</Mono>}
          />
          <Row
            label="Cash contribution"
            value={<Mono>−{formatAmount(view.pricing.cash_contribution)}</Mono>}
          />
          <Row
            label="Amount requested"
            value={
              <Mono className="font-semibold">
                {formatAmount(view.pricing.requested_principal)}
              </Mono>
            }
          />
          <Row
            label="Expected delivery"
            value={<Mono>{formatDay(view.delivery.expected_on)}</Mono>}
          />
        </dl>
      </Card>

      <AddresseeInformationRequestsCard caseId={view.id} audience="borrower" />
      <DocumentsCard caseId={view.id} audience="borrower" canUpload />
      <TimelineCard caseId={view.id} audience="borrower" />

      <p className="text-xs text-neutral-400">
        Contact on file: {view.buyer_contact_name}. Questions about the
        equipment or price go to {view.dealer.display_name}; questions about
        financing go to Tex.
      </p>
    </section>
  );
}

/** Where the buyer stands in the application: not started, part-way (resume), or submitted. */
function BorrowerApplicationCard({ view }: { view: BorrowerCaseView }) {
  const app = useApplication(view.id);
  if (app.isPending)
    return <p className="text-sm text-neutral-500">Loading your application…</p>;
  if (app.isError) return <Alert tone="error">{errorMessage(app.error)}</Alert>;
  const a = app.data;
  const done = a.completed_steps.length;
  const total = Object.keys(STEP_LABELS).length;

  if (a.submitted) {
    return (
      <Card
        title="Your application"
        actions={
          <Link
            to="/cases/$caseId/apply"
            params={{ caseId: view.id }}
            search={{ step: "review" }}
            className="text-xs text-tex-blue hover:underline"
          >
            View what you submitted
          </Link>
        }
      >
        <p className="text-sm">
          Submitted {formatDate(a.submitted.submitted_at)} as revision{" "}
          {a.submitted.revision_number}. Tex is reviewing it and will contact
          you at the email you signed in with.
        </p>
        {a.discrepancies.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-sm text-neutral-500">
              Tex will reconcile these differences with you and the dealer:
            </p>
            <DiscrepancyList items={a.discrepancies} />
          </div>
        )}
      </Card>
    );
  }

  if (!a.editable) {
    return (
      <Alert tone="info">
        This request is no longer taking an application.
      </Alert>
    );
  }

  return (
    <Card title="Your application">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          {done === 0 ? (
            <p>
              Only you and Tex see the application. The dealership sees that it
              was submitted — never your answers. It takes about ten minutes and
              saves as you go.
            </p>
          ) : (
            <p>
              {done} of {total} steps saved
              {a.updated_at ? ` · last saved ${formatDate(a.updated_at)}` : ""}.
              Pick up where you left off.
            </p>
          )}
        </div>
        <Link
          to="/cases/$caseId/apply"
          params={{ caseId: view.id }}
          search={{}}
          className="inline-flex items-center justify-center rounded-md bg-tex-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          {done === 0 ? "Start the application" : "Continue the application"}
        </Link>
      </div>
    </Card>
  );
}

function CaseDetail({ me, view }: { me: MeResponse; view: StaffOrDealerView }) {
  const perms = permissionsFor(me, view);
  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/cases"
            search={{}}
            className="text-sm text-neutral-500 hover:text-neutral-800"
          >
            ← Requests
          </Link>
          <h1 className="mt-1 flex flex-wrap items-center gap-3 font-display text-3xl font-bold text-neutral-900">
            <Mono>{view.reference}</Mono>
            <StageBadge stage={view.stage} />
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            {view.buyer_business_name} · opened {formatDate(view.created_at)}
            {view.audience === "tex" && <> · {view.dealer_legal_name}</>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {view.audience === "tex" && (
            <Link
              to="/tex/cases/$caseId"
              params={{ caseId: view.id }}
              search={{}}
              className="text-sm text-tex-blue hover:underline"
            >
              Open in underwriting desk →
            </Link>
          )}
          {perms.canWithdraw && <WithdrawControl view={view} />}
        </div>
      </div>

      {perms.readOnlyReason && (
        <Alert tone="info">{perms.readOnlyReason}</Alert>
      )}
      {view.stage === "closed" && view.close_reason && (
        <Alert tone="info">
          Closed: {view.close_reason}
          {view.audience === "tex" && view.close_note
            ? ` — ${view.close_note}`
            : ""}
        </Alert>
      )}
      {view.audience === "tex" && view.specialist_review_required && (
        <Alert tone="info">
          Specialist review required (trade-in or other flag on this request).
        </Alert>
      )}

      {view.audience === "dealer" && OFFER_STAGES.includes(view.stage) && (
        <OffersCard caseId={view.id} audience="dealer" stage={view.stage} />
      )}
      {view.audience === "dealer" && CLOSING_STAGES.includes(view.stage) && (
        <ClosingCard caseId={view.id} audience="dealer" />
      )}
      <LeadCard view={view} canEdit={perms.canEdit} />
      <ParticipantsCard
        caseId={view.id}
        participants={view.participants}
        defaultEmail={view.buyer_contact_email}
        defaultName={view.buyer_contact_name}
        canInvite={perms.canInvite}
        canRevoke={perms.canRevoke}
      />
      <EquipmentCard view={view} canEdit={perms.canEdit} />
      <PricingCard view={view} canEdit={perms.canEdit} />
      {view.audience === "dealer" && (
        <AddresseeInformationRequestsCard caseId={view.id} audience="dealer" />
      )}
      <DocumentsCard
        caseId={view.id}
        audience={view.audience}
        canUpload={perms.canUploadDocuments}
      />
      {view.audience === "tex" && <EligibilityCard caseId={view.id} />}
      <TimelineCard caseId={view.id} audience={view.audience} />

      <p className="text-xs text-neutral-400">
        Case <Mono>{view.id}</Mono> · version <Mono>{view.version}</Mono> ·
        updated {formatDate(view.updated_at)}
      </p>
    </section>
  );
}

function EditButton({
  editing,
  onClick,
  disabled,
}: {
  editing: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="secondary"
      onClick={onClick}
      disabled={disabled}
    >
      {editing ? "Cancel" : "Edit"}
    </Button>
  );
}

// ── lead: buyer + delivery + note ──

function LeadCard({
  view,
  canEdit,
}: {
  view: StaffOrDealerView;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const update = useUpdateLead(view.id);
  const [buyer, setBuyer] = useState<BuyerDraft>(() => buyerDraftFrom(view));
  const [delivery, setDelivery] = useState<DeliveryDraft>(() =>
    deliveryDraftFrom(view),
  );
  const [note, setNote] = useState(view.dealer_note ?? "");
  const [formError, setFormError] = useState<string | null>(null);

  const start = () => {
    setBuyer(buyerDraftFrom(view));
    setDelivery(deliveryDraftFrom(view));
    setNote(view.dealer_note ?? "");
    setFormError(null);
    update.reset();
    setEditing(true);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      update.mutate(
        {
          expected_version: view.version,
          buyer: toBuyerInput(buyer),
          delivery: toDeliveryInput(delivery),
          dealer_note: note.trim() || null,
        },
        { onSuccess: () => setEditing(false) },
      );
    } catch (err) {
      if (err instanceof FormError) setFormError(err.message);
      else throw err;
    }
  };

  const addr = view.buyer_address;
  return (
    <Card
      title="Buyer and delivery"
      actions={
        canEdit ? (
          <EditButton
            editing={editing}
            onClick={editing ? () => setEditing(false) : start}
            disabled={update.isPending}
          />
        ) : undefined
      }
    >
      {editing ? (
        <form onSubmit={submit} className="space-y-4">
          <BuyerFields
            draft={buyer}
            set={setBuyer}
            disabled={update.isPending}
          />
          <DeliveryFields
            draft={delivery}
            set={setDelivery}
            disabled={update.isPending}
          />
          <Field label="Note for Tex">
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={update.isPending}
            />
          </Field>
          {formError && <Alert tone="error">{formError}</Alert>}
          {update.isError && (
            <Alert tone="error">{writeError(update.error)}</Alert>
          )}
          <div className="flex justify-end">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save buyer details"}
            </Button>
          </div>
        </form>
      ) : (
        <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
          <dl>
            <Row label="Business" value={view.buyer_business_name} />
            <Row
              label="Entity type"
              value={
                view.buyer_entity_type
                  ? ENTITY_TYPE_LABELS[view.buyer_entity_type]
                  : null
              }
            />
            <Row label="Contact" value={view.buyer_contact_name} />
            <Row
              label="Email"
              value={<Mono>{view.buyer_contact_email}</Mono>}
            />
            <Row
              label="Phone"
              value={
                view.buyer_contact_phone ? (
                  <Mono>{view.buyer_contact_phone}</Mono>
                ) : null
              }
            />
            <Row
              label="Address"
              value={
                addr
                  ? [
                      addr.line1,
                      addr.line2,
                      `${addr.city}, ${addr.state} ${addr.postal_code}`,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : null
              }
            />
          </dl>
          <dl>
            <Row label="Buyer state" value={view.buyer_state} />
            <Row label="Equipment use state" value={view.equipment_use_state} />
            <Row
              label="Expected delivery"
              value={<Mono>{formatDay(view.delivery.expected_on)}</Mono>}
            />
            <Row
              label="Payment before delivery"
              value={
                view.delivery.pre_delivery_payment_requested
                  ? "Requested"
                  : "No"
              }
            />
            <Row
              label="Application"
              value={
                view.audience === "dealer"
                  ? view.application_submitted
                    ? "Submitted by buyer"
                    : "Not yet submitted"
                  : view.application
                    ? `Revision ${view.application.revision_number}`
                    : "Not yet submitted"
              }
            />
            <Row label="Note for Tex" value={view.dealer_note} />
          </dl>
        </div>
      )}
    </Card>
  );
}

// ── equipment ──

function EquipmentCard({
  view,
  canEdit,
}: {
  view: StaffOrDealerView;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const replace = useReplaceEquipment(view.id);
  const [lines, setLines] = useState<EquipmentDraft[]>(() =>
    equipmentDraftFrom(view.equipment),
  );
  const [formError, setFormError] = useState<string | null>(null);

  const start = () => {
    setLines(equipmentDraftFrom(view.equipment));
    setFormError(null);
    replace.reset();
    setEditing(true);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      replace.mutate(
        { expected_version: view.version, equipment: toEquipmentInput(lines) },
        { onSuccess: () => setEditing(false) },
      );
    } catch (err) {
      if (err instanceof FormError) setFormError(err.message);
      else throw err;
    }
  };

  return (
    <Card
      title="Equipment"
      actions={
        canEdit ? (
          <EditButton
            editing={editing}
            onClick={editing ? () => setEditing(false) : start}
            disabled={replace.isPending}
          />
        ) : undefined
      }
    >
      {editing ? (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-xs text-neutral-500">
            Saving replaces the whole equipment list and recomputes the totals.
          </p>
          <EquipmentEditor
            lines={lines}
            set={setLines}
            disabled={replace.isPending}
          />
          {formError && <Alert tone="error">{formError}</Alert>}
          {replace.isError && (
            <Alert tone="error">{writeError(replace.error)}</Alert>
          )}
          <div className="flex justify-end">
            <Button type="submit" disabled={replace.isPending}>
              {replace.isPending ? "Saving…" : "Save equipment"}
            </Button>
          </div>
        </form>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="py-1 pr-4">#</th>
              <th className="py-1 pr-4">Unit</th>
              <th className="py-1 pr-4">Condition</th>
              <th className="py-1 pr-4">Serial</th>
              <th className="py-1 pr-4 text-right">Qty</th>
              <th className="py-1 pr-4 text-right">Unit price</th>
              <th className="py-1 text-right">Line total</th>
            </tr>
          </thead>
          <tbody>
            {view.equipment.map((e) => (
              <tr key={e.id} className="border-t border-neutral-100 align-top transition-colors hover:bg-neutral-50/60">
                <td className="py-2 pr-4 text-neutral-500">
                  <Mono>{e.position}</Mono>
                </td>
                <td className="py-2 pr-4">
                  <div className="font-medium">
                    {[e.model_year, e.make, e.model]
                      .filter(Boolean)
                      .join(" ") || e.category}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {e.category}
                    {e.hours_or_miles !== null && (
                      <> · {e.hours_or_miles.toLocaleString()} hrs/mi</>
                    )}
                    {e.title_kind && (
                      <>
                        {" "}
                        · title: {humanize(e.title_kind)}
                        {e.vehicle_class ? ` (${e.vehicle_class})` : ""}
                      </>
                    )}
                    {e.existing_lien_noted && <> · existing lien</>}
                  </div>
                  {e.description && (
                    <div className="mt-1 text-xs text-neutral-500">
                      {e.description}
                    </div>
                  )}
                  {e.listing_url && (
                    <a
                      href={e.listing_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-tex-blue hover:underline"
                    >
                      Listing
                    </a>
                  )}
                </td>
                <td className="py-2 pr-4">{humanize(e.condition)}</td>
                <td className="py-2 pr-4">
                  {e.serial_number ? <Mono>{e.serial_number}</Mono> : "—"}
                </td>
                <td className="py-2 pr-4 text-right">
                  <Mono>{e.quantity}</Mono>
                </td>
                <td className="py-2 pr-4 text-right">
                  <Mono>{formatAmount(e.unit_price)}</Mono>
                </td>
                <td className="py-2 text-right">
                  <Mono>{formatAmount(e.line_total)}</Mono>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ── pricing ──

function PricingCard({
  view,
  canEdit,
}: {
  view: StaffOrDealerView;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const update = useUpdatePricing(view.id);
  const [draft, setDraft] = useState<PricingDraft>(() =>
    pricingDraftFrom(view),
  );
  const [formError, setFormError] = useState<string | null>(null);
  const preview = useMemo(
    () => previewPricing(equipmentDraftFrom(view.equipment), draft),
    [view.equipment, draft],
  );
  const edit = (fn: (prev: PricingDraft) => PricingDraft) => {
    setDraft(fn);
    setFormError(null);
    if (update.isError) update.reset();
  };

  const start = () => {
    setDraft(pricingDraftFrom(view));
    setFormError(null);
    update.reset();
    setEditing(true);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      const pricing = toPricingInput(draft);
      if (!preview.ok) {
        setFormError(preview.message);
        return;
      }
      update.mutate(
        { expected_version: view.version, pricing },
        { onSuccess: () => setEditing(false) },
      );
    } catch (err) {
      if (err instanceof FormError) setFormError(err.message);
      else throw err;
    }
  };

  const p = view.pricing;
  return (
    <Card
      title="Pricing"
      actions={
        canEdit ? (
          <EditButton
            editing={editing}
            onClick={editing ? () => setEditing(false) : start}
            disabled={update.isPending}
          />
        ) : undefined
      }
    >
      {editing ? (
        <form
          onSubmit={submit}
          className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]"
        >
          <div className="space-y-4">
            <PricingEditor
              draft={draft}
              set={edit}
              disabled={update.isPending}
            />
            {formError && <Alert tone="error">{formError}</Alert>}
            {update.isError && (
              <Alert tone="error">{writeError(update.error)}</Alert>
            )}
            <div className="flex justify-end">
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save pricing"}
              </Button>
            </div>
          </div>
          <div>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
              Preview
            </h3>
            <PricingSummary preview={preview} />
          </div>
        </form>
      ) : (
        <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
          <dl>
            <Row
              label="Equipment"
              value={<Mono>{formatAmount(p.equipment_total)}</Mono>}
            />
            <Row
              label="Soft costs"
              value={<Mono>{formatAmount(p.financed_soft_costs)}</Mono>}
            />
            <Row
              label="Discount"
              value={<Mono>−{formatAmount(p.discount)}</Mono>}
            />
            <Row
              label="Invoice total"
              value={
                <Mono className="font-semibold">
                  {formatAmount(p.invoice_total)}
                </Mono>
              }
            />
            <Row
              label="Cash contribution"
              value={<Mono>−{formatAmount(p.cash_contribution)}</Mono>}
            />
            <Row
              label="Requested financing"
              value={
                <Mono className="font-semibold">
                  {formatAmount(p.requested_principal)}
                </Mono>
              }
            />
            {p.reconciliation_reason && (
              <Row label="Override reason" value={p.reconciliation_reason} />
            )}
            <Row
              label="Trade-in"
              value={p.trade_in_noted ? "Noted — specialist review" : "None"}
            />
          </dl>
          <div>
            <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-500">
              Soft costs
            </h3>
            {view.soft_costs.length === 0 ? (
              <p className="text-sm text-neutral-500">None financed.</p>
            ) : (
              <dl>
                {view.soft_costs.map((s) => (
                  <Row
                    key={s.id}
                    label={
                      s.label
                        ? `${SOFT_COST_LABELS[s.kind]} — ${s.label}`
                        : SOFT_COST_LABELS[s.kind]
                    }
                    value={<Mono>{formatAmount(s.amount)}</Mono>}
                  />
                ))}
              </dl>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── withdraw ──

function WithdrawControl({ view }: { view: StaffOrDealerView }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const withdraw = useWithdrawCase(view.id);

  if (!open) {
    return (
      <Button type="button" variant="danger" onClick={() => setOpen(true)}>
        Withdraw request
      </Button>
    );
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        withdraw.mutate(
          { expected_version: view.version, reason: reason.trim() },
          { onSuccess: () => setOpen(false) },
        );
      }}
      className="w-full max-w-md space-y-2 rounded-md border border-red-200 bg-red-50 p-3"
    >
      <Field
        label="Why is this request being withdrawn?"
        hint="Recorded on the case; the buyer's application is not deleted."
      >
        <Textarea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          minLength={2}
          disabled={withdraw.isPending}
        />
      </Field>
      {withdraw.isError && (
        <Alert tone="error">{writeError(withdraw.error)}</Alert>
      )}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setOpen(false)}
          disabled={withdraw.isPending}
        >
          Keep it open
        </Button>
        <Button
          type="submit"
          variant="danger"
          disabled={withdraw.isPending || reason.trim().length < 2}
        >
          {withdraw.isPending ? "Withdrawing…" : "Withdraw"}
        </Button>
      </div>
    </form>
  );
}
